import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Sun, Moon, Cloud, Zap, ArrowRight, ArrowLeft, Activity,
  TrendingUp, Users, Leaf, RefreshCw, Radio
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LiveSimulation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const intervalRef = useRef(null);

  const fetchLive = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/simulation/live`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLive();
    intervalRef.current = setInterval(() => {
      fetchLive();
      setTick(t => t + 1);
    }, 15000); // Refresh every 15s
    return () => clearInterval(intervalRef.current);
  }, []);

  const getTimeIcon = (hour) => {
    if (hour >= 6 && hour < 21) return <Sun className="w-5 h-5 text-amber-500" />;
    return <Moon className="w-5 h-5 text-indigo-400" />;
  };

  const getWeatherIcon = (label) => {
    if (label?.includes('Nuageux')) return <Cloud className="w-5 h-5 text-gray-400" />;
    return <Sun className="w-5 h-5 text-amber-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  const d = data || {};
  const hour = d.hour || 0;
  const isNight = hour < 6 || hour > 21;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-green-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="back-dashboard">
            <ArrowLeft className="w-5 h-5 text-green-700" />
            <span className="font-semibold text-green-700">Dashboard</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full border border-green-200">
              <Radio className="w-3.5 h-3.5 text-green-600 animate-pulse" />
              <span className="text-xs font-semibold text-green-700" data-testid="live-badge">SIMULATION EN DIRECT</span>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchLive} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#052e16] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Flux d'Énergie en Direct
              </h1>
              <div className="flex items-center gap-3 mt-2 text-[#64748b]">
                {getTimeIcon(hour)}
                <span>{Math.floor(hour)}h{String(Math.round((hour % 1) * 60)).padStart(2, '0')}</span>
                <span className="text-green-300">|</span>
                {getWeatherIcon(d.weather_label)}
                <span>{d.weather_label}</span>
                <span className="text-green-300">|</span>
                <span>Facteur solaire: {Math.round((d.solar_factor || 0) * 100)}%</span>
              </div>
            </div>
          </div>

          {/* Global Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="global-stats">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Sun className="w-5 h-5 text-amber-600" />
                  <span className="text-xs text-amber-600 font-medium">Production</span>
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-production">
                  {d.total_production_kw || 0} kW
                </p>
                <p className="text-xs text-amber-600/70 mt-1">{isNight ? 'Pas de production nocturne' : 'Énergie disponible'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  <span className="text-xs text-blue-600 font-medium">Consommé</span>
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-consumption">
                  {d.total_consumption_kw || 0} kW
                </p>
                <p className="text-xs text-blue-600/70 mt-1">Distribué aux consommateurs</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-50 to-gray-100/50 border-gray-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Activity className="w-5 h-5 text-gray-500" />
                  <span className="text-xs text-gray-500 font-medium">Demande</span>
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-demand">
                  {d.total_demand_kw || 0} kW
                </p>
                <p className="text-xs text-gray-500/70 mt-1">Besoin total des ménages</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="text-xs text-green-600 font-medium">Surplus</span>
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="total-surplus">
                  {d.total_surplus_kwh || 0} kWh
                </p>
                <p className="text-xs text-green-600/70 mt-1">Non consommé</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 border-emerald-200 rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Leaf className="w-5 h-5 text-emerald-600" />
                  <span className="text-xs text-emerald-600 font-medium">CO2 évité</span>
                </div>
                <p className="text-2xl font-bold text-[#052e16]" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="co2-saved">
                  {d.co2_saved_today_kg || 0} kg
                </p>
                <p className="text-xs text-emerald-600/70 mt-1">Aujourd'hui</p>
              </CardContent>
            </Card>
          </div>

          {/* Rule reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-center gap-3 text-sm text-amber-800" data-testid="no-storage-rule">
            <Zap className="w-4 h-4 shrink-0" />
            <span>Pas de stockage : la consommation est <strong>toujours plafonnée</strong> par la production instantanée des producteurs.</span>
          </div>

          {/* 24h Curve + Live Transactions */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2 bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="hourly-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Production / Consommation / Demande (24h)
                </CardTitle>
                <p className="text-xs text-[#64748b]">La consommation (bleu) ne dépasse jamais la production (jaune)</p>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={d.hourly_curve || []}>
                      <defs>
                        <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="consGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#dcfce7" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} axisLine={false} unit=" kW" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
                          border: '1px solid #dcfce7', borderRadius: '12px',
                        }}
                      />
                      <Area type="monotone" dataKey="production" name="Production (kW)" stroke="#f59e0b" strokeWidth={2} fill="url(#prodGrad)" />
                      <Area type="monotone" dataKey="consumption" name="Consommé (kW)" stroke="#3b82f6" strokeWidth={2} fill="url(#consGrad)" />
                      <Area type="monotone" dataKey="demand" name="Demande (kW)" stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="5 5" fill="none" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Live Transactions Feed */}
            <Card className="bg-white border border-green-100 shadow-sm rounded-2xl" data-testid="live-transactions">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-green-500 animate-pulse" />
                  <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Transactions en direct
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                  {(d.live_transactions || []).length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Moon className="w-8 h-8 mx-auto mb-2 text-indigo-300" />
                      <p className="text-sm">Pas de transactions en cours</p>
                      <p className="text-xs">La production reprend au lever du soleil</p>
                    </div>
                  ) : (
                    (d.live_transactions || []).map((tx, i) => (
                      <div key={i} className="flex items-center gap-2 p-2.5 bg-green-50/50 rounded-xl border border-green-100 text-xs animate-in fade-in duration-500" style={{ animationDelay: `${i * 50}ms` }}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Sun className="w-3 h-3 text-amber-500 shrink-0" />
                            <span className="font-medium text-[#052e16] truncate">{tx.producer?.split(' ')[0]}</span>
                            <ArrowRight className="w-3 h-3 text-green-500 shrink-0" />
                            <Zap className="w-3 h-3 text-blue-500 shrink-0" />
                            <span className="font-medium text-[#052e16] truncate">{tx.consumer?.split(' ')[0]}</span>
                          </div>
                          <p className="text-[10px] text-[#64748b] mt-0.5 truncate">{tx.community}</p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-green-700" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {tx.energy_kwh} kWh
                          </p>
                          <p className="text-[10px] text-[#64748b]">{tx.amount_eur} EUR</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Per-Community Breakdown */}
          <h2 className="text-xl font-bold text-[#052e16] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Détail par communauté
          </h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="community-breakdown">
            {(d.communities || []).map((comm) => (
              <Card key={comm.id} className="bg-white border border-green-100 shadow-sm rounded-2xl hover:shadow-md transition-all" data-testid={`community-${comm.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold">{comm.name}</CardTitle>
                      <p className="text-xs text-[#64748b]">{comm.location}</p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-[#64748b]">
                      <Users className="w-3.5 h-3.5" />
                      {comm.member_count}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Production vs Consumption bars */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1"><Sun className="w-3 h-3 text-amber-500" /> Production</span>
                      <span className="font-mono font-medium">{comm.production_kw} kW</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-1000"
                        style={{ width: `${Math.min(100, (comm.production_kw / Math.max(comm.production_kw, comm.demand_kw || 0.01, 0.01)) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-blue-500" /> Consommé</span>
                      <span className="font-mono font-medium">{comm.consumption_kw} kW</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-400 rounded-full transition-all duration-1000"
                        style={{ width: `${Math.min(100, (comm.consumption_kw / Math.max(comm.production_kw, 0.01)) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1"><Activity className="w-3 h-3 text-gray-400" /> Demande</span>
                      <span className="font-mono font-medium text-gray-500">{comm.demand_kw || 0} kW</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gray-300 rounded-full transition-all duration-1000"
                        style={{ width: `${Math.min(100, ((comm.demand_kw || 0) / Math.max(comm.production_kw, comm.demand_kw || 0.01, 0.01)) * 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Autoconsommation + Coverage */}
                  <div className="flex items-center justify-between pt-2 border-t border-green-50">
                    <span className="text-xs text-[#64748b]">Taux d'autoconso.</span>
                    <span className={`text-sm font-bold ${comm.autoconsommation_rate > 70 ? 'text-green-600' : comm.autoconsommation_rate > 40 ? 'text-amber-600' : 'text-red-500'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {comm.autoconsommation_rate}%
                    </span>
                  </div>

                  {/* Members preview with coverage */}
                  <div className="pt-2 border-t border-green-50">
                    <p className="text-[10px] uppercase tracking-widest text-[#64748b] font-semibold mb-2">Membres actifs</p>
                    <div className="flex flex-wrap gap-1">
                      {(comm.producers || []).map((p, i) => (
                        <span key={`p-${i}`} className="inline-flex items-center gap-0.5 text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">
                          <Sun className="w-2.5 h-2.5" /> {p.name.split(' ')[0]} <span className="font-mono">{p.instant_kw}kW</span>
                        </span>
                      ))}
                      {(comm.consumers || []).map((c, i) => (
                        <span key={`c-${i}`} className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full border border-blue-200">
                          <Zap className="w-2.5 h-2.5" /> {c.name.split(' ')[0]} <span className="font-mono">{c.instant_kw}kW</span>
                          <span className="text-[9px] text-blue-400">({c.coverage || 0}%)</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
